import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { SimpleModal } from "src/app/components";
import { useGetOrdersById, useSemRushKeywords } from "src/app/hooks";
import { SiloStepsList } from "./SiloStepsList";
import { Button } from "react-bootstrap";

import { ISemRushKeywords } from "src/app/types/semrush";
import { TLangType } from "src/app/types/LangType";
import { getKeywordFromString } from "src/app/utils/keywordsHelper";
import { toast } from "react-toastify";
import OrdersList from "./OrdersList";
import { BulkSiloContainerProps, currentInformation, IData } from "./BulkSiloUtils";
import { OrderTransport } from "src/app/transports/order";
import SiloStepModalForm from "./siloStepModalForm";
import AllSiloOrderInfoFrom from "./AllSiloOrderInfoFrom";

const NUMBER_OF_KEYWORDS = 40;

const BulkSiloModalContainer = (props: BulkSiloContainerProps) => {
    const { handleClose = () => {}, selectedRowsId, openModal = false } = props;
    const { formatMessage } = useIntl();

    let defaultCurrentSteps = {};
    selectedRowsId.forEach((id) => (defaultCurrentSteps = { ...defaultCurrentSteps, [id]: 0 }));

    const [loading, setLoading] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<number>(selectedRowsId[0]);
    const [currentStep, setCurrentStep] = useState<{ [orderId: number]: number }>(
        defaultCurrentSteps
    );

    const [data, setData] = useState<Array<IData>>([]);

    const { orders, loading: loadingOrder } = useGetOrdersById(selectedRowsId);

    const { loading: keywordLoading, refresh } = useSemRushKeywords();

    const {
        currentOrder,
        currentOrderKeyword,
        hasOneOrMoreKeywords,
        currentData,
        currentOrderIndex,
        currentDataIndex,
    } = useMemo(
        () => currentInformation(selectedOrderId, data, orders),
        [selectedOrderId, data, orders]
    );

    useEffect(() => {
        if (!currentOrder || !currentOrder.moneySite || !currentOrderKeyword) {
            return;
        }

        if (!currentData) {
            createNewDataElement();
            return;
        }

        if (currentData?.semrushKeywords === false || currentData?.semrushKeywords) return;
    }, [selectedOrderId, orders, currentData]);

    function setSemrushKeywordsToData(
        semrushKeywords: ISemRushKeywords[] | false,
        error: string | null
    ) {
        const auxData = [...data];
        const auxSemrushKeywords = semrushKeywords ? [...semrushKeywords] : false;

        auxData[currentDataIndex].semrushKeywords = auxSemrushKeywords;
        auxData[currentDataIndex].selectedKeywords = undefined;

        if (error) {
            auxData[currentDataIndex].semrushError = error;
        }

        setData(auxData);
    }

    function setLastSelectedKeywordsInData(keywords: string[]) {
        const auxData = [...data];
        auxData[currentDataIndex].lastSelectedKeywords = keywords;
        setData(auxData);
    }

    function setLastNumberOfSiloInData(numberOfSilo?: number) {
        const auxData = [...data];
        auxData[currentDataIndex].lastNumberOfSilo = numberOfSilo;
        setData(auxData);
    }

    function handleSelectedKeyword(keyword: string) {
        if (!currentData?.semrushKeywords) return;
        const auxData = [...data];

        if (keyword === "REMOVE_KEYWORDS") {
            auxData[currentDataIndex].selectedKeywords = [];
        } else if (keyword === "ALL_KEYWORDS") {
            const allKeywords = currentData?.semrushKeywords?.map((el) => el.keyword || "");
            auxData[currentDataIndex].selectedKeywords = allKeywords;
        } else {
            const auxSelectedKeywords = [...(auxData[currentDataIndex].selectedKeywords || [])];

            const keywordIndex = auxSelectedKeywords.findIndex((el) => el === keyword);
            if (keywordIndex === -1) {
                auxSelectedKeywords.push(keyword);
                auxData[currentDataIndex].selectedKeywords = auxSelectedKeywords;
            } else {
                auxData[currentDataIndex].selectedKeywords = [
                    ...auxSelectedKeywords.splice(keywordIndex, 1),
                ];
            }
        }

        const allKeyword = [
            ...(auxData[currentDataIndex].selectedKeywords || []),
            ...(auxData[currentDataIndex].userKeywords
                ? getKeywordFromString(auxData[currentDataIndex].userKeywords || "")
                : []),
        ];

        auxData[currentDataIndex].allKeywords = allKeyword;

        setData(auxData);
    }

    function handleUserKeyword(keywords: string) {
        const auxData = [...data];

        const allKeyword = [
            ...(auxData[currentDataIndex]?.selectedKeywords || []),
            ...getKeywordFromString(keywords),
        ];

        auxData[currentDataIndex].allKeywords = allKeyword;
        auxData[currentDataIndex].userKeywords = keywords;

        setData(auxData);
    }

    function handleNumberOfSilo(numberOfSilo: number) {
        const auxData = [...data];
        auxData[currentDataIndex].numberOfSilo = numberOfSilo;
        setData(auxData);
    }

    function createNewDataElement() {
        setData((val) => [
            ...val,
            {
                id: selectedOrderId,
                url: currentOrder?.url,
                numberOfSilo: NUMBER_OF_KEYWORDS,
                selectedKeywords: undefined,
                userKeywords: undefined,
                semrushKeywords: undefined,
                semrushError: undefined,
            },
        ]);
    }

    const stepsList = SiloStepsList({
        data: currentData,
        handleSelectedKeyword,
        handleUserKeyword,
        loading,
        handleNumberOfSilo,
    });
    const numberOfSteps = stepsList.length;

    function goToStep(step: number) {
        if (!currentOrder || !currentOrderKeyword || loadingOrder) return;

        if (step > numberOfSteps - 1 || step < 0) return;

        if (step === 1) {
            if (
                currentData?.lastNumberOfSilo !== currentData?.numberOfSilo &&
                step > currentStep[selectedOrderId]
            ) {
                if (!currentOrder || !currentOrder.moneySite) return;

                const lang: TLangType = currentOrder.moneySite.lang;

                refresh(
                    currentOrderKeyword,
                    currentData?.numberOfSilo || NUMBER_OF_KEYWORDS,
                    lang,
                    setSemrushKeywordsToData
                );
                setLastNumberOfSiloInData(currentData?.numberOfSilo);
            }
        }

        if (step === 2) {
            if (!currentOrder || !currentOrderKeyword || !currentOrder.moneySite) return;

            let allKeywords = currentData?.selectedKeywords || [];
            if (currentData?.userKeywords) {
                allKeywords = [...allKeywords, ...getKeywordFromString(currentData.userKeywords)];
            }

            if (allKeywords.length === 0) {
                toast.error(`${formatMessage({ id: "common.notification.error.noKeywords" })}`);
                return;
            }

            //check if keywords ware changed when we go back
            if (
                allKeywords.filter((x) => !currentData?.lastSelectedKeywords?.includes(x)).length >
                    0 ||
                allKeywords.length !== currentData?.lastSelectedKeywords?.length
            ) {
                setLastSelectedKeywordsInData(allKeywords);
            }
        }

        setCurrentStep((val) => ({ ...val, [selectedOrderId]: step }));
    }

    async function onSubmit() {
        if (!hasOneOrMoreKeywords) {
            toast.error("Error no keywords found");
            return;
        }
        setLoading(true);
        try {
            await OrderTransport.createBulkBoostSiloOrders(data);
            toast.success(formatMessage({ id: "common.notification.success" }));
            handleClose();
        } catch (error) {
            toast.error(formatMessage({ id: "common.notification.error" }));
        } finally {
            setLoading(false);
        }
    }

    function nextToNextOrder() {
        if (!orders) return;

        const nextOrder =
            currentOrderIndex === orders?.length - 1 ? -1 : orders[currentOrderIndex + 1].id;
        setSelectedOrderId(nextOrder);
    }

    return (
        <>
            {openModal && (
                <SimpleModal
                    entityName={formatMessage({ id: "common.entity.SiloStrategy" })}
                    handleClose={handleClose}
                    loading={loading || loadingOrder || keywordLoading}
                    formId=""
                    disableConfirmation={!hasOneOrMoreKeywords}
                    buttonConfirmationLabel={formatMessage({ id: "common.action.confirmation" })}
                    buttonConfirmationAction={onSubmit}
                    showModal={true}
                    modalProps={{ fullscreen: true }}
                >
                    <div className="bulkSiloModalContainerWrapper">
                        {orders && (
                            <div className="grid">
                                <OrdersList
                                    orders={orders}
                                    selectedOrderId={selectedOrderId}
                                    setSelectedOrderId={setSelectedOrderId}
                                />
                                <div>
                                    <div className="forms">
                                        {selectedOrderId === -1 ? (
                                            <AllSiloOrderInfoFrom data={data} />
                                        ) : (
                                            <SiloStepModalForm
                                                currentStep={currentStep[selectedOrderId]}
                                                stepsList={stepsList}
                                            />
                                        )}
                                    </div>
                                    {selectedOrderId !== -1 && (
                                        <div className="button-Wrapper">
                                            <Button
                                                onClick={() => {
                                                    goToStep(currentStep[selectedOrderId] - 1);
                                                }}
                                            >
                                                Back
                                            </Button>

                                            {currentStep[selectedOrderId] !== numberOfSteps - 1 ? (
                                                <Button
                                                    onClick={() =>
                                                        goToStep(currentStep[selectedOrderId] + 1)
                                                    }
                                                >
                                                    {currentStep[selectedOrderId] <
                                                    numberOfSteps - 2
                                                        ? "Continue"
                                                        : "getInfo"}
                                                </Button>
                                            ) : (
                                                <Button onClick={nextToNextOrder}>Next</Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </SimpleModal>
            )}
        </>
    );
};

export default BulkSiloModalContainer;
